import React, { useEffect, useMemo, useRef, useState } from 'react';
import {Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputLabel, List, ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Select, Stack, TextField, ToggleButton, Tooltip, Typography, styled} from "@mui/material";
import _ from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import OrdersDND from './OrdersDND';
import deliveryZoneServices from 'app/services/delivery-zone-services';
import ZonesMapEdit from '../delivery_zones/ZonesMapEdit';
import { LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import centreServices from 'app/services/centre-services';
import { StyledDatePicker } from 'app/widgets/Shared/StyledDatePicker/StyledDatePicker';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { LoadingButton } from '@mui/lab';
import OrderDeliveryDetailsPopover from 'app/widgets/Deliveries/OrderDeliveryDetailsPopover';
import planificationServices from 'app/services/planification-services';
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined';
import InsertCommentOutlinedIcon from '@mui/icons-material/InsertCommentOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import LocationSearchingOutlinedIcon from '@mui/icons-material/LocationSearchingOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { geoCoords, optimizerNames, pointInPolygon } from 'app/utils/appHelpers';
import { useSnackbar } from 'notistack';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Menu from '@mui/material/Menu';
import CarrierPickupSearch from './CarrierPickupSearch';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import PrimaryAddressChip from 'app/shared/widgets/PrimaryAddressChip';
import shippingServices from 'app/services/shipping-services';
import orderServices from 'app/services/order-services';
import { useNavigate } from "react-router-dom";
import { getDropoffCarrierByKey, getDropoffCarrierOptions } from 'app/utils/dropoffCarriers';
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';
import { ORDER_SOURCES } from 'app/utils/orderSources';
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip
} from "recharts";

const formatQty = (value) => Number(value || 0).toLocaleString('es-CL');
const ROUTES_LIMIT_COLORS = Object.freeze({
    limit: '#F59E0B',
    used: '#7C3AED',
    overBy: '#DC2626',
    remaining: '#D1D5DB'
});
const PENDING_ORDERS_PAGE_SIZE = 100;
// Valor del selector "Planificar por" que no corresponde a un plugin, sino al modo
// de elegir pedidos a mano.
const OPTIMIZE_BY_ORDERS = 'selected_orders';

// Margen para juntar cambios de fecha seguidos en una sola consulta.
const DATE_FILTER_DEBOUNCE_MS = 600;

// Fechas y busqueda que se dejan guardadas al apretar Guardar, para encontrar la vista
// igual la proxima vez que se entra. Una clave por empresa, como la del centro.
const planningFiltersKey = (companyId) => `planificationFilters${companyId}`;
const readPlanningFilters = (companyId) => {
    try {
        const saved = JSON.parse(localStorage.getItem(planningFiltersKey(companyId)) || 'null');
        if (!saved) return {};
        const toDate = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d) ? d : null; };
        return {
            startDate: toDate(saved.startDate),
            endDate: toDate(saved.endDate),
            searchQuery: typeof saved.searchQuery === 'string' ? saved.searchQuery : '',
        };
    } catch (e) {
        return {};
    }
};

// Mapa visible u oculto, una clave por empresa. Si nunca se toco, el plan Distribuidor
// parte oculto: terceriza las entregas y no le sirve el mapa.
const showMapKey = (companyId) => `planificationShowMap${companyId}`;
const readShowMap = (company) => {
    try {
        const saved = localStorage.getItem(showMapKey(company?.id));
        if (saved !== null) return saved === 'true';
    } catch (e) {}
    return company?.subscription_plan_slug !== 'distribuidor';
};

// El date picker sólo avisaba en onAccept (calendario), así que la fecha
// digitada a mano se perdía. Con onChange tomamos también lo tipeado, pero
// recién cuando ya es una fecha completa: mientras se escribe el año, MUI
// entrega años parciales (0002, 0020, 0202) que dispararían búsquedas de más.
const isCompleteDate = (value) => {
    if (!(value instanceof Date) || isNaN(value.getTime())) { return false }
    const year = value.getFullYear();
    return year >= 1900 && year <= 2100;
};

const RoutesLimitTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
            }}
        >
            {payload.map((item) => (
                <Typography key={item.name} variant="body2" sx={{ color: item.color }}>
                    {item.name}: {formatQty(item.value)}
                </Typography>
            ))}
        </Box>
    );
};

const PlanificationIndex = () => {
    const {authUser} = useJumboAuth();
    const { companySettings } = useCompanySettings();
    const navigate = useNavigate();
    const dropoffCarrierOptions = useMemo(
        () => getDropoffCarrierOptions(companySettings?.available_dropoff_carriers),
        [companySettings?.available_dropoff_carriers]
    );
    const [pendingOrders, setPendingOrders] = useState([])
    const [pendingOrdersViewPage, setPendingOrdersViewPage] = useState(1)
    const [pendingOrdersMeta, setPendingOrdersMeta] = useState({
        current_page: 1,
        next_page: null,
        prev_page: null,
        total_pages: 1,
        total_count: 0,
        per_page: PENDING_ORDERS_PAGE_SIZE,
    })
    const [pendingOrdersLoadingMore, setPendingOrdersLoadingMore] = useState(false)
    const [savedPlanningFilters] = useState(() => readPlanningFilters(authUser?.current_company?.id))
    const [searchQuery, setSearchQuery] = useState(savedPlanningFilters.searchQuery || '')
    const [listLoading, setListLoading] = useState(false)
    const [drawingMode, setDrawingMode] = useState({ mode: 'polygon', cancel: false })
    const [deliveryZones, setDeliveryZones] = useState([])
    const [currDeliveryZone, setCurrDeliveryZone] = useState(null)
    const [confirmDeleteZone, setConfirmDeleteZone] = useState(null)
    const [shouldSaveCurrDeliveryZone, setShouldSaveCurrDeliveryZone] = useState(false)
    const [loadingZones, setLoadingZones] = useState(false)
    const [startDate, setStartDate] = useState(savedPlanningFilters.startDate || new Date())
    const [endDate, setEndDate] = useState(savedPlanningFilters.endDate || new Date())
    // Filtros de los pedidos por planificar: origen (Order.with_source) y dropoff
    // ('' todos, 'con', 'sin' o 'carrier:<key>').
    const [filterSource, setFilterSource] = useState('')
    const [filterDropoff, setFilterDropoff] = useState('')
    const [availableSourceKeys, setAvailableSourceKeys] = useState(null)
    const [centres, setCentres] = useState([])
    const [currCentre, setCurrCentre] = useState(null)
    const [trips, setTrips] = useState([])
    const [saving, setSaving] = useState(false)
    const [itemOnHover, setItemOnHover] = useState(null)
    const [currDisplayCentre, setCurrDisplayCentre] = useState(null)
    const [showMap, setShowMap] = useState(() => readShowMap(authUser?.current_company))
    const [showZones, setShowZones] = useState(true)
    const [showProductDetails, setShowProductDetails] = useState(true)
    const [showCustomerLocationOnHover, setShowCustomerLocationOnHover] = useState(true)
    const [routes, setRoutes] = useState([])
    const [hiddenTripsRoutes, setHiddenTripsRoutes] = useState([])
    const [drivers, setDrivers] = useState([])
    const [transporters, setTransporters] = useState([])
    const [vehicles, setVehicles] = useState([])
    const [loadingCentreOptions, setLoadingCentreOptions] = useState(false)
    const [selectedBoxes, setSelectedBoxes] = useState([])
    // Modal de elección de dirección al planificar (clientes con >1 dirección).
    const [openAddressDialog, setOpenAddressDialog] = useState(false)
    const [addressResolveQueue, setAddressResolveQueue] = useState([])
    const [pendingMovement, setPendingMovement] = useState(null)
    // 'drag' = el modal viene de arrastrar cajas; 'optimize' = de recalcular.
    const [addressDialogMode, setAddressDialogMode] = useState('drag')
    // Movimientos que dejó pendientes el optimizador mientras se eligen direcciones.
    const optimizerPlanRef = useRef(null)
    const [currMapCenter, setCurrMapCenter] = useState(null)
    const [currOptimizer, setCurrOptimizer] = useState(null)
    // Modo "Pedidos": en vez de optimizar todo el centro, se marcan con checkbox
    // los pedidos por planificar y sólo esos entran al optimizador.
    const [optimizeByOrders, setOptimizeByOrders] = useState(false)
    const [selectedOrderIdsToOptimize, setSelectedOrderIdsToOptimize] = useState([])
    const [optimizing, setOptimizing] = useState(false)
    const [moveDestination, setMoveDestination] = useState(null)
    const { enqueueSnackbar } = useSnackbar();
    const [carrierMenu, setCarrierMenu] = useState({
        anchorEl: null,
        tripIndex: null,
    });
    const [mapPickupMode, setMapPickupMode] = useState(null);
    // Bifurcación que se ofrece cuando el carrier elegido tiene dirección de
    // dropoff por defecto configurada en company_settings.
    const [dropoffDefaultPrompt, setDropoffDefaultPrompt] = useState({
        open: false,
        carrier: null,
        carrierLabel: '',
        tripIndex: null,
        defaultAddress: null,
        // Pedidos de la ruta que recibirán la dirección por defecto. Parten
        // todos marcados para no cambiar el comportamiento de siempre, pero se
        // pueden desmarcar uno por uno.
        selectedOrderIds: [],
    });
    const [pickupPoints, setPickupPoints] = useState([]);
    const [loadingPickupPoints, setLoadingPickupPoints] = useState(false);
    const [activeTripIndex, setActiveTripIndex] = useState(null);
    const pickupSearchRequestRef = useRef(0);
    const pickupSearchGeocoderRef = useRef(null);
    const planificationRequestRef = useRef(0);
    // La proxima carga disparada por listLoading trae solo los pendientes. Las rutas planificadas
    // del centro no dependen de las fechas ni de la busqueda, y recargarlas pisaba lo que se
    // habia armado y no se habia guardado.
    const pendingOnlyNextLoadRef = useRef(false);
    const [subscriptionLimitModal, setSubscriptionLimitModal] = useState({
        open: false,
        message: '',
        used: 0,
        limit: 0,
        overBy: 0
    });

    // Identidad de la ruta dentro de la pantalla, que no depende de su posicion en la
    // lista. Es la key de React, y por eso existe: con la key puesta por indice, al
    // planificar la Ruta 1 la que quedaba pasaba a ocupar su lugar y React reusaba el
    // componente de la ruta que ya no estaba. El value de las Autocomplete de MUI si
    // llegaba vacio, pero el texto que el input guarda aparte no, asi que la ruta que
    // quedaba se veia con el vehiculo y el conductor de la anterior mientras por dentro
    // los tenia en null: el boton de finalizar aparecia deshabilitado sin motivo visible.
    //
    // No sirve el id del viaje: las rutas recien armadas todavia no tienen uno.
    const newPlanningTripUid = () => _.uniqueId('planningTrip_');

    const createPlanningTrip = (orders = []) => ({
        uid: newPlanningTripUid(),
        id: null,
        ship_date: moment().startOf('day'),
        driver: {},
        vehicle: {},
        driver_id: null,
        vehicle_id: null,
        orders,
    });

    const normalizePlanningTrip = (trip = {}) => ({
        ...trip,
        uid: trip?.uid ?? newPlanningTripUid(),
        driver_id: trip?.driver_id ?? null,
        vehicle_id: trip?.vehicle_id ?? null,
        orders: trip?.orders || [],
    });

    const findTripAssigneeById = (userId) =>
        _.find(_.uniqBy([...drivers, ...transporters], 'id'), (user) => String(user?.id) === String(userId));

    // [
    //     { droppableId: 'pendingOrders', index: 2 },
    //     { droppableId: 'pendingOrders', index: 4 },
    //     ...
    // ]

    useEffect(() => {
        console.warn = console.error = () => {};
        setShowZones(localStorage.getItem('showZones') === 'false' ? false : true)
        setShowProductDetails(localStorage.getItem('showProductDetails') === 'false' ? false : true)
        setShowCustomerLocationOnHover(localStorage.getItem('showCustomerLocationOnHover') === 'false' ? false : true)
        loadCentres()
    }, [])

    useEffect(() => {
        if(listLoading){
            const pendingOrdersOnly = pendingOnlyNextLoadRef.current
            pendingOnlyNextLoadRef.current = false
            loadPlanificationData({ pendingOrdersOnly })
        }
    }, [listLoading])

    useEffect(() => {
        if(loadingZones){
            loadDeliveryZones()
        }
    }, [loadingZones])

    useEffect(() => {
        if(loadingCentreOptions){
            loadCentreOptions()
        }
    }, [loadingCentreOptions])

    // Cambiar centro o fechas trae otra lista de pendientes: lo marcado antes ya
    // no está a la vista y no se puede desmarcar.
    useEffect(() => {
        setSelectedOrderIdsToOptimize([])
    }, [currCentre, startDate, endDate, filterSource, filterDropoff])

    // El origen ofrece solo los que la compañia tiene en algun pedido. Mientras no llega, todos.
    useEffect(() => {
        let cancelled = false
        orderServices.getSources()
            .then((data) => { if (!cancelled) setAvailableSourceKeys((data?.sources || []).map((source) => source.key)) })
            .catch(() => { if (!cancelled) setAvailableSourceKeys(null) })
        return () => { cancelled = true }
    }, [authUser?.current_company?.id])

    // Cambiar un filtro trae otra lista de pendientes, como las fechas pero sin esperar.
    const filtersMountedRef = useRef(false)
    useEffect(() => {
        if (!filtersMountedRef.current) { filtersMountedRef.current = true; return }
        if (!currCentre) { return }
        pendingOnlyNextLoadRef.current = true
        setListLoading(true)
    }, [filterSource, filterDropoff])


    // Esperamos a que el usuario termine de mover las fechas antes de consultar:
    // editar día y mes por separado, o Desde y Hasta seguidos, es una sola
    // intención y debería costar una sola consulta.
    useEffect(() => {
        if(!currCentre) { return }
        const timer = setTimeout(() => { pendingOnlyNextLoadRef.current = true; setListLoading(true) }, DATE_FILTER_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [startDate, endDate])

    useEffect(() => {
        if(currCentre) {
            pendingOnlyNextLoadRef.current = false
            setListLoading(true)
            setLoadingZones(true)
            setLoadingCentreOptions(true)
            localStorage.setItem(`currCentre${authUser?.current_company?.id}`, JSON.stringify(currCentre))
        }
    }, [currCentre])

    useEffect(() => {
        refreshRoutes()
    }, [trips])

    useEffect(() => {
        if(optimizing && selectedBoxes.length > 0) {
            var DNDEvent = {
                source: { droppableId: 'pendingOrders', index: 0 },
                destination: moveDestination
            }
            onDragEnd(DNDEvent)
        }
    }, [selectedBoxes])

    // La empresa puede llegar despues del primer render: ahi recien se sabe el plan.
    const currentCompanyId = authUser?.current_company?.id
    useEffect(() => {
        if (currentCompanyId) setShowMap(readShowMap(authUser?.current_company))
    }, [currentCompanyId])

    const toggleShowMap = () => {
        const next = !showMap
        setShowMap(next)
        try { localStorage.setItem(showMapKey(currentCompanyId), next) } catch (e) {}
    }

    // Elegir el punto de retiro del carrier se hace en el mapa: si estaba oculto, se muestra.
    useEffect(() => {
        if (mapPickupMode) setShowMap(true)
    }, [mapPickupMode])

    useEffect(() => {
        localStorage.setItem('showZones', showZones)
    }, [showZones])

    useEffect(() => {
        localStorage.setItem('showProductDetails', showProductDetails)
    }, [showProductDetails])

    useEffect(() => {
        localStorage.setItem('showCustomerLocationOnHover', showCustomerLocationOnHover)
    }, [showCustomerLocationOnHover])

    const [mapMode, setMapMode] = useState({
        type: 'default', // default | carrier_pickup
        carrier: null,   // 'mercadolibre' | 'starken' | etc
        tripIndex: null, // qué ruta está configurando
    });

    const onSelectCarrier = (carrier, tripIndex) => {
        closeCarrierMenu();

        const catalogCarrier = getDropoffCarrierByKey(carrier, companySettings?.available_dropoff_carriers);
        const defaultAddress = catalogCarrier?.defaultAddress;

        // Sin dirección por defecto geocodificada no hay nada que ofrecer: la
        // dirección puede haberse guardado sin coordenadas si HERE no la encontró.
        if (defaultAddress && _.isFinite(Number(defaultAddress.lat)) && _.isFinite(Number(defaultAddress.lng))) {
            setDropoffDefaultPrompt({
                open: true,
                carrier,
                carrierLabel: catalogCarrier?.label || carrier,
                tripIndex,
                defaultAddress,
                selectedOrderIds: (trips[tripIndex]?.orders || []).map((order) => order.id),
            });
            return;
        }

        setMapPickupMode({ carrier, tripIndex });
    };

    const closeDropoffDefaultPrompt = () => {
        setDropoffDefaultPrompt({
            open: false,
            carrier: null,
            carrierLabel: '',
            tripIndex: null,
            defaultAddress: null,
            selectedOrderIds: [],
        });
    };

    const dropoffPromptOrders = useMemo(() => {
        const { open, tripIndex } = dropoffDefaultPrompt;
        if (!open || tripIndex === null || tripIndex === undefined) return [];
        return trips[tripIndex]?.orders || [];
    }, [dropoffDefaultPrompt.open, dropoffDefaultPrompt.tripIndex, trips]);

    const toggleDropoffPromptOrder = (orderId) => {
        setDropoffDefaultPrompt((prev) => ({
            ...prev,
            selectedOrderIds: _.xor(prev.selectedOrderIds || [], [orderId]),
        }));
    };

    const toggleAllDropoffPromptOrders = () => {
        setDropoffDefaultPrompt((prev) => {
            const allIds = dropoffPromptOrders.map((order) => order.id);
            const allSelected = allIds.length > 0 && allIds.length === (prev.selectedOrderIds || []).length;
            return {
                ...prev,
                selectedOrderIds: allSelected ? [] : allIds,
            };
        });
    };

    // La dirección por defecto se asigna a los pedidos marcados en el diálogo,
    // no punto por punto como en el flujo manual. Cuando la ruta tiene un solo
    // pedido no hay nada que elegir y se asigna a ese.
    const applyDefaultDropoffToTrip = () => {
        const { carrier, tripIndex, defaultAddress, selectedOrderIds } = dropoffDefaultPrompt;
        if (tripIndex === null || tripIndex === undefined) return;

        const tempTrips = _.cloneDeep(trips);
        const tripOrders = tempTrips[tripIndex]?.orders || [];

        if (tripOrders.length === 0) {
            enqueueSnackbar('La ruta no tiene pedidos a los que asignar el dropoff', { variant: 'info' });
            closeDropoffDefaultPrompt();
            return;
        }

        const targetOrders = tripOrders.filter((order) => _.includes(selectedOrderIds || [], order.id));

        // Sin pedidos marcados no se toca nada: el diálogo queda abierto para
        // que se pueda marcar alguno o cancelar.
        if (targetOrders.length === 0) {
            enqueueSnackbar('Selecciona al menos un pedido para asignarle la dirección', { variant: 'warning' });
            return;
        }

        targetOrders.forEach((order) => {
            order.dropoff = true;
            order.dropoff_carrier = carrier;
            order.dropoff_point = {
                id: `dropoff_default_${carrier}_${defaultAddress.locationId || 'na'}`,
                name: 'Dirección por defecto',
                lat: Number(defaultAddress.lat),
                lng: Number(defaultAddress.lng),
                carrier,
                address_hint: defaultAddress.text,
            };
        });

        setTrips(tempTrips);
        closeDropoffDefaultPrompt();
        enqueueSnackbar(
            `Dropoff asignado a ${targetOrders.length} pedido(s) de la ruta`,
            { variant: 'success' }
        );
    };

    const startManualDropoffSelection = () => {
        const { carrier, tripIndex } = dropoffDefaultPrompt;
        closeDropoffDefaultPrompt();
        setMapPickupMode({ carrier, tripIndex });
    };

    const openCarrierMenu = (e, tripIndex) => {
        setActiveTripIndex(tripIndex);
        setCarrierMenu({ anchorEl: e.currentTarget, tripIndex });
    };

    const closeCarrierMenu = () => {
        setCarrierMenu({
            anchorEl: null,
            tripIndex: null,
        });
    };

    const getPickupSearchGeocoder = () => {
        if (pickupSearchGeocoderRef.current) return pickupSearchGeocoderRef.current;

        const GeocoderCtor = window?.google?.maps?.Geocoder;
        if (!GeocoderCtor) return null;

        pickupSearchGeocoderRef.current = new GeocoderCtor();
        return pickupSearchGeocoderRef.current;
    };

    const geocodePickupSearchAddress = async ({ address, carrier }) => {
        const normalizedAddress = String(address || '').trim();
        if (!normalizedAddress) return null;

        const geocoder = getPickupSearchGeocoder();
        if (!geocoder) return null;

        return new Promise((resolve) => {
            geocoder.geocode(
                {
                    address: normalizedAddress,
                    componentRestrictions: { country: 'CL' },
                    region: 'CL',
                },
                (results, status) => {
                    const okStatus = window?.google?.maps?.GeocoderStatus?.OK || 'OK';
                    if (status !== okStatus || !results?.length) {
                        resolve(null);
                        return;
                    }

                    const location = results[0]?.geometry?.location;
                    const lat = typeof location?.lat === 'function' ? location.lat() : null;
                    const lng = typeof location?.lng === 'function' ? location.lng() : null;

                    if (!_.isFinite(lat) || !_.isFinite(lng)) {
                        resolve(null);
                        return;
                    }

                    resolve({
                        id: `pickup_search_${carrier || 'carrier'}_${Date.now()}`,
                        name: 'Dirección buscada (estimado)',
                        lat,
                        lng,
                        carrier,
                        address_hint: results[0]?.formatted_address || normalizedAddress,
                    });
                }
            );
        });
    };


    const onPickupPointClick = async (point) => {
        if (mapPickupMode?.tripIndex === null || mapPickupMode?.tripIndex === undefined) return;

        const tripIndex = mapPickupMode.tripIndex;
        const tripDroppableId = `plannedOrders_${tripIndex}`;

        const selectedIdxs = selectedBoxes
            .filter((sb) => sb.droppableId === tripDroppableId)
            .map((sb) => sb.index);

        if (selectedIdxs.length === 0) {
            enqueueSnackbar('Selecciona al menos una orden dentro de la ruta para asignar dropoff', { variant: 'info' });
            return;
        }

        const tempTrips = _.cloneDeep(trips);

        selectedIdxs.forEach((idx) => {
            const order = tempTrips[tripIndex].orders[idx];
            console.log('Asignando dropoff a order:', order.id);

            order.dropoff = true;
            order.dropoff_carrier = point.carrier;
            order.dropoff_point = {
                id: point.id,
                name: point.name,
                lat: point.lat,
                lng: point.lng,
                carrier: point.carrier,
                address_hint: point.address_hint,
            };
        });

        setTrips(tempTrips);
        setSelectedBoxes([]);
        setMapPickupMode(null);
        setPickupPoints([]);
    };

    const hasAnyDropoff = trips.some(trip =>
        trip.orders?.some(order => order?.dropoff)
    );

    const selectedIndexesForTrip = (tripIndex) => {
        const droppableId = `plannedOrders_${tripIndex}`;

        return selectedBoxes
            .filter((sb) => sb.droppableId === droppableId)
            .map((sb) => sb.index);
    };

    const orderHasMissingBarcode = (order) =>
        order?.order_details?.some(od => !od.product_barcode);

    const printDuration = (_secs) => {
        var hhs = _.floor(_secs/3600)
        var mms = _.floor((_secs - hhs*3600)/60)
        return `${hhs > 0 ? `${hhs}h y ` : ''}${mms}min`
    }

    const pendingOrderMatchesSearch = (order, rawQuery) => {
        const q = String(rawQuery || '').trim().toLowerCase();
        if (!q) return true;

        const attrs = [
            order?.code,
            order?.locatable?.name,
            (order?.effective_location || order?.locatable?.location)?.short_zone,
            order?.comment
        ];

        return _.some(attrs, (attr) => _.includes(String(attr || '').toLowerCase(), q));
    };

    const buildOrderParams = ({
        pendingOrdersPage = 1,
        pendingOrdersPerPage = PENDING_ORDERS_PAGE_SIZE,
        pendingOrdersOnly = false,
        searchQueryOverride = undefined,
    } = {}) => {
        let par = []
        const effectiveSearchQuery = typeof searchQueryOverride === 'string' ? searchQueryOverride : searchQuery;
        if(effectiveSearchQuery && effectiveSearchQuery !== 0) {
            par.push(`q%5Bcode_or_locatable_of_Customer_type_name_or_locatable_of_Place_type_name_cont%5D=${effectiveSearchQuery}`)
        }
        if(startDate) {
            par.push(`q%5Bship_date_gteq%5D=${startDate}`)
        }
        if(endDate) {
            par.push(`q%5Bship_date_lteq%5D=${endDate}`)
        }
        par.push(`q%5Bcentre_id_eq%5D=${currCentre?.id}`)
        if (filterSource) {
            par.push(`q%5Bwith_source%5D=${filterSource}`)
        }
        if (filterDropoff === 'con') {
            par.push('q%5Bdropoff_true%5D=1')
        } else if (filterDropoff === 'sin') {
            par.push('q%5Bdropoff_false%5D=1')
        } else if (filterDropoff.startsWith('carrier:')) {
            par.push(`q%5Bdropoff_carrier_eq%5D=${encodeURIComponent(filterDropoff.slice('carrier:'.length))}`)
        }
        par.push(`pending_orders_page=${pendingOrdersPage}`)
        par.push(`pending_orders_per_page=${Math.min(Number(pendingOrdersPerPage || PENDING_ORDERS_PAGE_SIZE), PENDING_ORDERS_PAGE_SIZE)}`)
        if (pendingOrdersOnly) {
            par.push('pending_orders_only=true')
        }

        par = _.join(par, '&')

        return `?${par}`;
    }

    const isSubscriptionLimitReachedError = (err, featureKey = 'rutas') => {
        const responseData = err?.response?.data;
        return (
            responseData?.error_code === 'subscription_limit_reached' &&
            responseData?.feature === featureKey
        );
    };

    const openSubscriptionLimitModal = (err) => {
        const responseData = err?.response?.data || {};
        const used = Number(responseData?.used || 0);
        const limit = Number(responseData?.limit || 0);
        const overBy = Math.max(used - limit, 0);

        setSubscriptionLimitModal({
            open: true,
            message: responseData?.error || 'Alcanzaste el limite de rutas de tu plan actual.',
            used,
            limit,
            overBy
        });
    };

    const buildRoutesLimitChartData = (used, limit) => {
        const safeUsed = Number(used || 0);
        const safeLimit = Number(limit || 0);
        const overBy = Math.max(safeUsed - safeLimit, 0);

        if (safeLimit > 0 && overBy > 0) {
            return [
                { name: 'Límite plan', value: safeLimit, color: ROUTES_LIMIT_COLORS.limit },
                { name: 'Sobreconsumo', value: overBy, color: ROUTES_LIMIT_COLORS.overBy }
            ];
        }

        if (safeLimit > 0) {
            return [
                { name: 'Usado', value: safeUsed, color: ROUTES_LIMIT_COLORS.used },
                { name: 'Disponible', value: Math.max(safeLimit - safeUsed, 0), color: ROUTES_LIMIT_COLORS.remaining }
            ];
        }

        return [{ name: 'Usado', value: safeUsed, color: ROUTES_LIMIT_COLORS.used }];
    };

    const normalizePendingOrdersMeta = (meta) => {
        const normalized = meta || {};

        return {
            current_page: Number(normalized.current_page || 1),
            next_page: normalized.next_page ? Number(normalized.next_page) : null,
            prev_page: normalized.prev_page ? Number(normalized.prev_page) : null,
            total_pages: Number(normalized.total_pages || 1),
            total_count: Number(normalized.total_count || 0),
            per_page: Number(normalized.per_page || PENDING_ORDERS_PAGE_SIZE),
        };
    };

    const pendingOrdersLoadedPages = Math.max(1, Math.ceil((pendingOrders?.length || 0) / PENDING_ORDERS_PAGE_SIZE));
    const pendingOrdersTotalPages = Math.max(
        Number(pendingOrdersMeta?.total_pages || 1),
        pendingOrdersLoadedPages
    );
    const pendingOrdersPageOffset = (Math.max(pendingOrdersViewPage, 1) - 1) * PENDING_ORDERS_PAGE_SIZE;
    const visiblePendingOrders = pendingOrders.slice(
        pendingOrdersPageOffset,
        pendingOrdersPageOffset + PENDING_ORDERS_PAGE_SIZE
    );
    const canGoToNextPendingOrdersPage =
        pendingOrdersViewPage < pendingOrdersLoadedPages || Boolean(pendingOrdersMeta?.next_page);

    // Cuando el cliente tiene más de una dirección, al planificar se elige cuál
    // usar y queda en el pedido como planned_address. La dirección del cliente
    // es sólo el fallback: usarla siempre ponía el pin y la ruta en la principal
    // aunque se hubiera elegido otra. Mismo criterio que Order#effective_lat.
    const orderCoords = (o) => ({
        lat: o?.planned_address?.lat ?? o?.locatable?.location?.lat,
        lng: o?.planned_address?.lng ?? o?.locatable?.location?.lng,
    })

    // Las mismas zonas que mira el optimizador: las del centro elegido más las
    // que aplican a todos los centros (centre_id nulo).
    const centreDeliveryZonePolygons = useMemo(() => (
        _.chain(deliveryZones)
            .filter((dz) => dz?.centre_id == null || String(dz.centre_id) === String(currCentre?.id))
            .map((dz) => geoCoords(dz?.location?.path))
            .filter((polygon) => polygon.length >= 3)
            .value()
    ), [deliveryZones, currCentre])

    // Un pedido sin zona de despacho no vuelve nunca del optimizador, así que no
    // tiene sentido dejar marcarlo: la lista lo muestra bloqueado.
    const orderHasDeliveryZone = (order) => {
        const coords = orderCoords(order)
        return _.some(centreDeliveryZonePolygons, (polygon) => pointInPolygon(coords, polygon))
    }

    const optimizableOrderIds = useMemo(() => (
        _.chain(pendingOrders).filter(orderHasDeliveryZone).map('id').value()
    ), [pendingOrders, centreDeliveryZonePolygons])

    // Si un pedido marcado deja de tener zona (se edita la zona, o se elige otra
    // dirección del cliente) se desmarca solo, para no mandarlo a optimizar.
    useEffect(() => {
        setSelectedOrderIdsToOptimize((curr) => {
            const kept = _.intersection(curr, optimizableOrderIds)
            return kept.length === curr.length ? curr : kept
        })
    }, [optimizableOrderIds])

    const pendingOrdersSelectionsForCurrentPage = () => {
        const start = pendingOrdersPageOffset;
        const end = start + visiblePendingOrders.length;

        return selectionsInGroup('pendingOrders')
            .filter((sb) => sb.index >= start && sb.index < end)
            .map((sb) => ({ ...sb, index: sb.index - start }));
    };

    const toPendingOrdersGlobalIndex = (idx) => {
        if (typeof idx !== 'number') return idx;
        return pendingOrdersPageOffset + idx;
    };

    const normalizePendingOrdersDnDResult = (result) => {
        if (!result) return result;

        const nextResult = _.cloneDeep(result);

        if (nextResult?.source?.droppableId === 'pendingOrders') {
            nextResult.source.index = toPendingOrdersGlobalIndex(nextResult.source.index);
        }
        if (nextResult?.destination?.droppableId === 'pendingOrders') {
            nextResult.destination.index = toPendingOrdersGlobalIndex(nextResult.destination.index);
        }

        return nextResult;
    };

    const loadPlanificationData = ({
        pendingOrdersPage = 1,
        appendPendingOrders = false,
        pendingOrdersOnly = false,
        searchQueryOverride = undefined,
    } = {}) => {
        const requestId = planificationRequestRef.current + 1;
        planificationRequestRef.current = requestId;

        if (appendPendingOrders) {
            setPendingOrdersLoadingMore(true);
        } else {
            setPendingOrdersLoadingMore(false);
            setPendingOrdersViewPage(1);
        }

        return planificationServices.getPlanification(
            buildOrderParams({
                pendingOrdersPage,
                pendingOrdersPerPage: PENDING_ORDERS_PAGE_SIZE,
                pendingOrdersOnly,
                searchQueryOverride,
            })
        )
            .then(data => {
                if (requestId !== planificationRequestRef.current) return;

                // Sin recargar las rutas, el backend sigue contando como pendientes los pedidos que
                // ya se arrastraron a una ruta no guardada: se sacan para que no salgan dos veces.
                const plannedIds = pendingOrdersOnly
                    ? new Set(_.flatMap(trips, (t) => _.map(t?.orders, 'id')))
                    : null;
                const incomingPendingOrders = (data?.pending_orders || [])
                    .filter((o) => !plannedIds || !plannedIds.has(o.id));
                const incomingPendingOrdersMeta = normalizePendingOrdersMeta(data?.pending_orders_meta);

                setPendingOrdersMeta(incomingPendingOrdersMeta);
                if (appendPendingOrders) {
                    setPendingOrders((prev) => {
                        const existingIds = new Set(prev.map((o) => o.id));
                        const newItems = incomingPendingOrders.filter((o) => !existingIds.has(o.id));
                        return [...prev, ...newItems];
                    });
                } else {
                    setPendingOrders(incomingPendingOrders);
                }

                if (!pendingOrdersOnly) {
                    setTrips((data.trips || []).map(normalizePlanningTrip))
                    setCurrDisplayCentre({
                        id: data.centre?.id,
                        code: data.centre?.code,
                        name: data.centre?.name,
                        location: data.centre?.main_centre_zone?.location
                    })
                    setCurrMapCenter({ lat: data.centre?.main_centre_zone?.location?.lat, lng: data.centre?.main_centre_zone?.location?.lng })
                }
            })
            .catch((err) => {
                if (requestId !== planificationRequestRef.current) return;

                if (isSubscriptionLimitReachedError(err)) {
                    setPendingOrders([])
                    setPendingOrdersMeta({
                        current_page: 1,
                        next_page: null,
                        prev_page: null,
                        total_pages: 1,
                        total_count: 0,
                        per_page: PENDING_ORDERS_PAGE_SIZE,
                    })
                    setTrips([])
                    setRoutes([])
                    openSubscriptionLimitModal(err)
                    return
                }
                if (appendPendingOrders) {
                    enqueueSnackbar('No fue posible cargar más pedidos por planificar', { variant: 'error' });
                }
                return null;
            })
            .finally(() => {
                if (requestId !== planificationRequestRef.current) return;

                if (appendPendingOrders) {
                    setPendingOrdersLoadingMore(false);
                } else {
                    setListLoading(false);
                }
            });
    }

    const loadMorePendingOrders = () => {
        if (listLoading || pendingOrdersLoadingMore) return;

        const nextPage = pendingOrdersMeta?.next_page;
        if (!nextPage) return;

        return loadPlanificationData({
            pendingOrdersPage: nextPage,
            appendPendingOrders: true,
            pendingOrdersOnly: true,
        });
    };

    const goToPendingOrdersPage = async (page) => {
        const targetPage = Number(page || 1);
        if (!Number.isFinite(targetPage)) return;

        if (targetPage < 1) return;
        if (targetPage > pendingOrdersTotalPages) return;
        if (targetPage === pendingOrdersViewPage) return;

        if (targetPage <= pendingOrdersLoadedPages) {
            setPendingOrdersViewPage(targetPage);
            return;
        }

        if (targetPage === pendingOrdersLoadedPages + 1 && pendingOrdersMeta?.next_page) {
            const res = await loadMorePendingOrders();
            if (res !== null) {
                setPendingOrdersViewPage(targetPage);
            }
        }
    };

    useEffect(() => {
        if (pendingOrdersLoadingMore) return;
        if ((pendingOrders?.length || 0) === 0) {
            if (pendingOrdersViewPage !== 1) setPendingOrdersViewPage(1);
            return;
        }

        const maxVisiblePage = Math.max(1, Math.ceil(pendingOrders.length / PENDING_ORDERS_PAGE_SIZE));
        if (pendingOrdersViewPage > maxVisiblePage) {
            setPendingOrdersViewPage(maxVisiblePage);
        }
    }, [pendingOrders.length, pendingOrdersLoadingMore, pendingOrdersViewPage]);

    const handlePendingOrdersCardSearchQueryChange = (newVal) => {
        const nextQuery = String(newVal || '');
        const trimmedQuery = nextQuery.trim();

        setSearchQuery(nextQuery);

        if (!trimmedQuery) {
            setPendingOrdersViewPage(1);
            if (String(searchQuery || '').trim()) {
                if (listLoading) {
                    loadPlanificationData({ searchQueryOverride: nextQuery, pendingOrdersOnly: true });
                } else {
                    pendingOnlyNextLoadRef.current = true;
                    setListLoading(true);
                }
            }
            return;
        }

        const localMatchIndex = _.findIndex(pendingOrders, (order) => pendingOrderMatchesSearch(order, trimmedQuery));
        if (localMatchIndex > -1) {
            setPendingOrdersViewPage(Math.floor(localMatchIndex / PENDING_ORDERS_PAGE_SIZE) + 1);
            return;
        }

        if (!currCentre?.id) return;

        setPendingOrdersViewPage(1);
        if (listLoading) {
            loadPlanificationData({ searchQueryOverride: nextQuery, pendingOrdersOnly: true });
        } else {
            pendingOnlyNextLoadRef.current = true;
            setListLoading(true);
        }
    };

    const refreshRoutes = () => {
        if(trips.length === 0) {
            setRoutes([])
            return
        }
        planificationServices.getRoutes({ centre_id: currCentre?.id, trips: trips })
            .then(data => {
                setRoutes(data)
            })
            .catch((err) => {

            });
    }

    const routeOrderErrorsByKey = (route) => {
        return _.keyBy(route?.order_errors || [], (orderError) => {
            return orderError.order_id ? `id:${orderError.order_id}` : `index:${orderError.index}`;
        });
    };

    const ordersWithRouteErrors = (orders, route) => {
        const errorsByKey = routeOrderErrorsByKey(route);

        return _.map(orders || [], (order, index) => {
            const error = errorsByKey[order?.id ? `id:${order.id}` : `index:${index}`];
            return {
                ...order,
                route_error: error?.error || null
            };
        });
    };

    const loadDeliveryZones = () => {
        deliveryZoneServices.getDeliveryZones("include=location")
            .then(data => {
                setDeliveryZones(data.data)
                setLoadingZones(false)
            })
            .catch((err) => {
                setLoadingZones(false)
            });
    }

    const loadCentres = () => {
        centreServices.getCentres()
            .then(data => {
                setCentres(data.data)
                if(data?.data && currCentre === null) {
                    if(!currCentre){
                        var lsCentre = JSON.parse(localStorage.getItem(`currCentre${authUser?.current_company?.id}`))
                        if(lsCentre) {
                            setCurrCentre(lsCentre)
                        } else {
                            setCurrCentre(data.data[0])
                        }
                    }
                }
            });
    }

    const selectionsInGroup = (droppableId) => {
        return _.filter(selectedBoxes, { droppableId: droppableId });
    };

    const selectionIndex = (droppableId, idx) => {
        return _.findIndex(selectedBoxes, { droppableId: droppableId, index: idx });
    };

    const isSelected = (droppableId, idx) => {
        return selectionIndex(droppableId, idx) > -1;
    };

    const toggleSelection = (droppableId, idx) => {

        let order = null;

        if (droppableId === 'pendingOrders') {
            order = pendingOrders[idx];
        } else if (droppableId.includes('plannedOrders')) {
            const tripIndex = parseInt(droppableId.split('_')[1]);
            order = trips[tripIndex]?.orders[idx];
        }

        console.log(
            `Click en pedido → ID: ${order?.id}, Código: ${order?.code}, grupo: ${droppableId}`
        );

        const oldSelectedBoxes = selectedBoxes;
        const wasSelected = isSelected(droppableId, idx);

        const newSelectedBoxes = (() => {
            if (!wasSelected) {
                return [{ droppableId: droppableId, index: idx }];
            }

            if (oldSelectedBoxes.length > 1) {
                return [{ droppableId: droppableId, index: idx }];
            }

            return [];
        })();

        setSelectedBoxes(newSelectedBoxes)
    };

    const toggleSelectionInGroup = (droppableId, idx) => {
        const oldSelectedBoxes = selectedBoxes
        const index = selectionIndex(droppableId, idx)
    
        // if not selected - add it to the selected items
        const newSelected = [...oldSelectedBoxes]

        if (index === -1) {
            newSelected.push({ droppableId: droppableId, index: idx })
            setSelectedBoxes(newSelected)
        } else {
            // it was previously selected and now needs to be removed from the group
            newSelected.splice(index, 1)
        }
        
        setSelectedBoxes(newSelected)
    };

    const loadCentreOptions = () => {
        centreServices.getCentreOptions(currCentre?.id)
            .then(data => {
                setDrivers(data?.data?.drivers || [])
                setTransporters(data?.data?.transporters || [])
                setVehicles((data?.data?.vehicles || []).filter((v) => v?.active !== false))
                setLoadingCentreOptions(false)
            })
            .catch((err) => {
                setLoadingCentreOptions(false)
            });
    }

    const onOverlayComplete = o => {
        var coordStr = _.join(_.map(o.overlay.getPath().getArray(), (c) => `${c.toUrlValue()}`), ';')
        var newDz = { id: null, name: 'Nueva zona', location: { path: coordStr } }

        setDeliveryZones([...deliveryZones, newDz])
        setCurrDeliveryZone(newDz)
        setShouldSaveCurrDeliveryZone(true)
        o.overlay.setMap(null)
    }

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
      
        return result;
    };

    const executeMovement = async(selecteds, result) => {
        if (!result.destination) return;
        
        var src   = { droppableId: result.source?.droppableId,      index: result.source?.index }
        var dest  = { droppableId: result.destination?.droppableId, index: result.destination?.index }
        
        var newTripIdx = null
        
        var tripsToSet = _.cloneDeep(trips)
        var pendingOrdersToSet = _.cloneDeep(pendingOrders)

        let warnedMissingBarcode = false;

        _.each(selecteds, (sb) => {
            src = { droppableId: sb.droppableId, index: sb.index }

            if (src.droppableId === dest.droppableId && src.index === dest.index) return;

            if(src.droppableId === 'pendingOrders') {
                // Box origin is pending order
                if(dest.droppableId === 'newTrip') {
                    var ord = pendingOrders[src.index]

                    if (!warnedMissingBarcode && orderHasMissingBarcode(ord)) {
                        enqueueSnackbar(
                            'Se está planificando un pedido con productos sin código de barras',
                            { variant: 'warning' }
                        );
                        warnedMissingBarcode = true;
                    }

                    if(newTripIdx === null) {
                        // Create new trip
                        var trip = createPlanningTrip([ord])
                        newTripIdx = tripsToSet.length
                        tripsToSet.push(trip)
                    } else {
                        // Add to already created new trip
                        tripsToSet[newTripIdx].orders = [...tripsToSet[newTripIdx].orders, ord]
                    }
                    pendingOrdersToSet = _.filter(pendingOrdersToSet, (el, i) => !_.isEqual(el, pendingOrders[src.index]))
                } else if(src.droppableId === dest.droppableId) {
                    // Reorder inside same group
                    pendingOrdersToSet = reorder(
                        pendingOrders,
                        src.index,
                        dest.index
                    );
                } else if(_.includes(dest.droppableId, 'plannedOrders')) {
                    // Move to existing trip
                    var tripIndex = parseInt(dest.droppableId.split('_')[1])
                    tripsToSet[tripIndex].orders.splice(dest.index, 0, pendingOrders[src.index])
                    pendingOrdersToSet = _.filter(pendingOrdersToSet, (el, i) => !_.isEqual(el, pendingOrders[src.index]))
                }
            } else {
                // Box origin is a trip
                var sourceTripIndex = parseInt(src.droppableId.split('_')[1])
                
                if(dest.droppableId === 'newTrip') {

                    var ord = trips[sourceTripIndex].orders[src.index]
                    if(newTripIdx === null) {
                        // Create new trip
                        var trip = createPlanningTrip([ord])
                        newTripIdx = tripsToSet.length
                        tripsToSet.push(trip)
                    } else {
                        // Add to already created new trip
                        tripsToSet[newTripIdx].orders = [...tripsToSet[newTripIdx].orders, ord]
                    }
                    tripsToSet[sourceTripIndex].orders = _.filter(tripsToSet[sourceTripIndex].orders, (el, i) => !_.isEqual(el, trips[sourceTripIndex].orders[src.index]))
                } else if(src.droppableId === dest.droppableId) {
                    // Reorder inside same trip
                    tripsToSet[sourceTripIndex].orders = reorder(
                        tripsToSet[sourceTripIndex].orders,
                        src.index,
                        dest.index
                    )
                } else if(_.includes(dest.droppableId, 'plannedOrders')) {
                    // Move to an other trip
                    var tripIndex = parseInt(dest.droppableId.split('_')[1])
                    tripsToSet[tripIndex].orders.splice(dest.index, 0, trips[sourceTripIndex].orders[src.index])
                    tripsToSet[sourceTripIndex].orders = _.filter(tripsToSet[sourceTripIndex].orders, (el, i) => !_.isEqual(el, trips[sourceTripIndex].orders[src.index]))
                } else if(dest.droppableId === 'pendingOrders') {
                    // Move to pending orders
                    pendingOrdersToSet.splice(dest.index, 0, trips[sourceTripIndex].orders[src.index])
                    tripsToSet[sourceTripIndex].orders = _.filter(tripsToSet[sourceTripIndex].orders, (el, i) => !_.isEqual(el, trips[sourceTripIndex].orders[src.index]))
                }
            }
        })
        setTripsAndRemoveEmpties(tripsToSet)
        setPendingOrders(pendingOrdersToSet)
    }

    const onDragEnd = (result) => {
        const normalizedResult = normalizePendingOrdersDnDResult(result)
        const dest = normalizedResult?.destination

        // Al planificar (mover a una ruta nueva o existente) un pedido cuyo
        // cliente tiene más de una dirección, primero pedimos qué dirección usar.
        // Durante una optimización no se pregunta acá: las direcciones ya se
        // resolvieron todas antes de empezar a mover, y abrir el modal a mitad de
        // la secuencia lo haría desaparecer con el movimiento siguiente.
        if (!optimizing && dest && (dest.droppableId === 'newTrip' || _.includes(dest.droppableId, 'plannedOrders'))) {
            const toResolve = _.chain(selectedBoxes)
                .filter((sb) => sb.droppableId === 'pendingOrders')
                .map((sb) => pendingOrders[sb.index])
                .filter((ord) => ord && (ord.locatable?.addresses?.length > 1) && !ord.planned_address)
                .value()

            if (toResolve.length > 0) {
                setPendingMovement({ selecteds: selectedBoxes, result: normalizedResult })
                setAddressDialogMode('drag')
                setAddressResolveQueue(toResolve)
                setOpenAddressDialog(true)
                return
            }
        }

        executeMovement(selectedBoxes, normalizedResult)
        setSelectedBoxes([])
    }

    // Se acabó la cola de pedidos por resolver: seguir con lo que la disparó.
    const finishAddressResolution = () => {
        setOpenAddressDialog(false)
        setAddressResolveQueue([])

        if (addressDialogMode === 'optimize') {
            const plan = optimizerPlanRef.current
            optimizerPlanRef.current = null
            runOptimizerPlan(plan)
            return
        }

        if (pendingMovement) {
            executeMovement(pendingMovement.selecteds, pendingMovement.result)
            setPendingMovement(null)
        }
        setSelectedBoxes([])
    }

    // El usuario eligió una dirección para el pedido actual de la cola.
    const chooseAddressForCurrentOrder = (addr) => {
        const order = addressResolveQueue[0]
        if (order) {
            // Se adjunta al objeto order (viaja en el payload de savePlanification
            // y lo lee executeMovement al armar la ruta).
            order.planned_address = {
                customer_address_id: addr.id,
                location_id: addr.location_id,
                lat: addr.lat,
                lng: addr.lng,
                short_address: addr.short_address,
                path: addr.path,
            }
        }
        const rest = addressResolveQueue.slice(1)
        if (rest.length > 0) {
            setAddressResolveQueue(rest)
            return
        }
        finishAddressResolution()
    }

    // Optimizando: omitir este pedido lo deja en pendientes y sigue con el resto.
    const skipCurrentAddressResolution = () => {
        const order = addressResolveQueue[0]
        if (order && optimizerPlanRef.current) {
            optimizerPlanRef.current = _.filter(optimizerPlanRef.current, (step) => step.order_id !== order.id)
        }
        const rest = addressResolveQueue.slice(1)
        if (rest.length > 0) {
            setAddressResolveQueue(rest)
            return
        }
        finishAddressResolution()
    }

    const cancelAddressResolution = () => {
        if (addressDialogMode === 'optimize') {
            skipCurrentAddressResolution()
            return
        }
        setOpenAddressDialog(false)
        setAddressResolveQueue([])
        setPendingMovement(null)
        setSelectedBoxes([])
    }

    const setTripsAndRemoveEmpties = (newVals, sourceTrip, sourceTripIndex) => {
        var valsToSet = _.filter(newVals, (el) => el.orders?.length > 0)
        setTrips(valsToSet, () => refreshRoutes())
    }

    const onDragStart = (e) => {
        const normalizedSource =
            e?.source?.droppableId === 'pendingOrders'
                ? { ...e.source, index: toPendingOrdersGlobalIndex(e.source.index) }
                : e.source;

        if(!isSelected(normalizedSource.droppableId, normalizedSource.index)) {
            console.log(normalizedSource.droppableId, normalizedSource.index)
            setSelectedBoxes([{ droppableId: normalizedSource.droppableId, index: normalizedSource.index }])
        }
    }

    const handleDeliveryZoneNameChange = (e) => {
        var newDz = {...currDeliveryZone, name: e.target.value}
        var deliveryZonesCopy = _.filter(deliveryZones, (el) => el != currDeliveryZone)
        deliveryZonesCopy.push(newDz)
        setDeliveryZones(deliveryZonesCopy)
        setCurrDeliveryZone(newDz)
        setShouldSaveCurrDeliveryZone(true)
    }

    const handleOnCloseInfoWindow = () => {
        if(currDeliveryZone.id === null) {
            var deliveryZonesCopy = _.filter(deliveryZones, (el) => el.location?.path !== currDeliveryZone.location?.path)
            setDeliveryZones(deliveryZonesCopy)
        }
        setCurrDeliveryZone(null)
    }

    // Una zona sin id todavía se está dibujando: descartarla no borra nada, así que
    // sólo se pide confirmación cuando la zona ya está guardada.
    const requestDeleteDeliveryZone = () => {
        if(currDeliveryZone?.id == null) {
            handleOnCloseInfoWindow()
        } else {
            setConfirmDeleteZone(currDeliveryZone)
        }
    }

    const deleteDeliveryZone = () => {
        setConfirmDeleteZone(null)
        if(currDeliveryZone.id === null) {
            handleOnCloseInfoWindow()
        } else {
            deliveryZoneServices.destroyDeliveryZone(currDeliveryZone.id)
                .then(data => {
                    var deliveryZonesCopy = _.filter(deliveryZones, (el) => el.id !== currDeliveryZone.id)
                    setDeliveryZones(deliveryZonesCopy)
                    setCurrDeliveryZone(null)
                })
                .catch((err) => {
                    setCurrDeliveryZone(null)
                });
        }
    }

    const saveDeliveryZone = (dz) => {
        if(shouldSaveCurrDeliveryZone && currDeliveryZone?.id) {
            // Update
            setLoadingZones(true)
            deliveryZoneServices.updateDeliveryZone(currDeliveryZone.id, "include=location", { name: currDeliveryZone.name })
                .then(data => {
                    var deliveryZonesCopy = _.filter(deliveryZones, (el) => el.id !== data.id)
                    deliveryZonesCopy.push(data)
                    setDeliveryZones(deliveryZonesCopy)
                    setLoadingZones(false)
                })
                .catch((err) => {
                    setLoadingZones(false)
                });
        } else if(shouldSaveCurrDeliveryZone) {
            // Create
            var selDZ = currDeliveryZone
            setLoadingZones(true)
            deliveryZoneServices.createDeliveryZone("include=location", { name: currDeliveryZone.name, location: { path: currDeliveryZone.location.path } })
                .then(data => {
                    var deliveryZonesCopy = _.filter(deliveryZones, (el) => el.location.path !== selDZ.location.path)
                    deliveryZonesCopy.push(data)
                    setDeliveryZones(deliveryZonesCopy)
                    setLoadingZones(false)
                })
                .catch((err) => {
                    setLoadingZones(false)
                });
        }
        setCurrDeliveryZone(null)
    }

    const savePlanification = (_tripsIndexes=null, endPlanification = false) => {

        setSaving(true)

        // El backend responde indexado por la posición dentro del payload enviado,
        // no por la posición en la lista completa de rutas. Cuando se planifica una
        // sola ruta hay que traducir esas posiciones a los índices originales, si no
        // se termina tocando la ruta equivocada.
        var sentIndexes = _tripsIndexes
            ? _.filter(_.range(trips.length), (i) => _.includes(_tripsIndexes, i))
            : _.range(trips.length)
        var _trips = _.map(sentIndexes, (i) => trips[i])

        var tripsToSave = [...trips]

        // El backend borra las rutas planificadas que no vengan en el payload, y eso solo
        // tiene sentido cuando le mandamos la lista completa. Al planificar una sola ruta
        // se manda un subconjunto: sin este flag, las demas rutas guardadas se borraban.
        var fullSync = !_tripsIndexes

        planificationServices.savePlanification({ centre_id: currCentre?.id, trips: _trips, end_planification: endPlanification, full_sync: fullSync })
            .then(data => {
                setSaving(false)
                if (fullSync) {
                    // Guardar tambien deja las fechas y la busqueda para la proxima vez.
                    try {
                        localStorage.setItem(planningFiltersKey(authUser?.current_company?.id), JSON.stringify({
                            startDate: startDate ? new Date(startDate).toISOString() : null,
                            endDate: endDate ? new Date(endDate).toISOString() : null,
                            searchQuery: searchQuery || '',
                        }))
                    } catch (e) {}
                }
                var tripsToRemove = []

                _.forOwn(data, (v,k) => {
                    var tripIdx = sentIndexes[Number(k)]
                    if(tripIdx === undefined) return

                    if(endPlanification) {
                        // Ending planification
                        if(v?.saved) {
                            // If any trip was saved, then we remove it from the list
                            tripsToRemove.push(tripIdx)
                        }
                    } else if(v?.saved && v?.id) {
                        // Saving planification
                        tripsToSave[tripIdx] = { ...tripsToSave[tripIdx], id: v?.id }
                    }
                })

                _.each(_.orderBy(tripsToRemove, [], ['desc']), (i) => {
                    tripsToSave.splice(i, 1)
                })
                setTrips(tripsToSave)
            })
            .catch((err) => {
                setSaving(false)
                // Antes el error se descartaba en silencio: la pantalla seguia mostrando
                // rutas que el backend no habia guardado y recien al recargar se notaba.
                var serverError = err?.response?.data?.error
                var msg = _.isArray(serverError) ? serverError.join('. ') : serverError
                enqueueSnackbar(
                    msg || 'No fue posible guardar la planificación. Recarga la página para ver el estado real.',
                    { variant: 'error', autoHideDuration: 6000 }
                )
            });
    }

    // Traduce la respuesta del optimizador a la lista de movimientos a ejecutar,
    // sin tocar todavía el estado: así se puede resolver antes lo que falte.
    const buildOptimizerPlan = (data) => {
        var _pendingOrders = _.cloneDeep(pendingOrders)
        var movements = []

        // Build a list with the movements to be done
        _.each(data, (group, i) => {
            _.each(group, (oid, j) => {
                // Un pedido repetido en dos grupos (zonas solapadas) se movería
                // dos veces y el segundo movimiento metería un undefined en la ruta.
                if(_.some(movements, { order_id: oid })) return
                var orderidx = _.findIndex(_pendingOrders, { id: oid })
                if(orderidx === -1) return
                movements.push({ trip_group: i, order_id: oid, order_idx: orderidx })
            })
        })

        // Sort the list by order_idx desc to not mess with the indexes: al sacar
        // un pedido de pendientes sólo se corren los índices mayores.
        return _.reverse(_.sortBy(movements, ['order_idx']))
    }

    // Ejecuta los movimientos espaciados para que el estado alcance a asentarse
    // entre uno y otro.
    const runOptimizerPlan = (plan) => {
        if(!plan || plan.length === 0) {
            setOptimizing(false)
            return
        }

        // Las rutas nuevas se numeran recién acá, y no al armar el plan, porque el
        // pedido omitido puede ser justo el que estrenaba la ruta de su grupo.
        var nextTripPos = trips.length
        var groupTripPos = {}
        var movements = _.map(plan, (step) => {
            if(groupTripPos[step.trip_group] === undefined) {
                groupTripPos[step.trip_group] = nextTripPos
                nextTripPos += 1
                return { ...step, destination: { droppableId: 'newTrip', index: 0 } }
            }
            return {
                ...step,
                destination: { droppableId: `plannedOrders_${groupTripPos[step.trip_group]}`, index: 0 },
            }
        })

        setOptimizing(true)
        _.each(movements, (mv, i) => {
            setTimeout(() => {
                setMoveDestination(mv.destination)
                setSelectedBoxes([{ droppableId: 'pendingOrders', index: mv.order_idx }])
            }, 300*i)
        })
        setTimeout(() => {
            setOptimizing(false)
            // Los que se planificaron se desmarcan; los omitidos siguen marcados
            // porque quedaron en la lista de pendientes.
            const movedIds = _.map(movements, 'order_id')
            setSelectedOrderIdsToOptimize((curr) => _.difference(curr, movedIds))
        }, 300*movements.length)
    }

    const toggleOrderToOptimize = (orderId) => {
        if(!_.includes(optimizableOrderIds, orderId)) return

        setSelectedOrderIdsToOptimize((curr) => (
            _.includes(curr, orderId) ? _.without(curr, orderId) : [...curr, orderId]
        ))
    }

    const optimizeTrips = () => {
        if(optimizeByOrders && selectedOrderIdsToOptimize.length === 0) {
            enqueueSnackbar('Marca al menos un pedido para optimizar', { variant: 'warning' })
            return
        }

        setOptimizing(true)
        planificationServices.optimize({
            centre_id: currCentre?.id,
            plugin_id: currOptimizer?.id,
            start_date: startDate,
            end_date: endDate,
            // Sin order_ids el backend optimiza todos los pendientes del centro.
            order_ids: optimizeByOrders ? selectedOrderIdsToOptimize : undefined,
        })
            .then(data => {
                const plan = buildOptimizerPlan(data)

                // Los pedidos de clientes con más de una dirección se preguntan
                // todos por adelantado: si se preguntara sobre la marcha, el
                // movimiento siguiente pisaría el modal antes de alcanzar a elegir.
                const toResolve = _.chain(plan)
                    .map((step) => pendingOrders[step.order_idx])
                    .filter((ord) => ord && (ord.locatable?.addresses?.length > 1) && !ord.planned_address)
                    .uniqBy('id')
                    .value()

                if(toResolve.length > 0) {
                    optimizerPlanRef.current = plan
                    setAddressDialogMode('optimize')
                    setAddressResolveQueue(toResolve)
                    setOpenAddressDialog(true)
                    return
                }

                runOptimizerPlan(plan)
            })
            .catch((err) => {
                setOptimizing(false)
            });
    }

    const visibleSources = ORDER_SOURCES.filter((source) => !availableSourceKeys || availableSourceKeys.includes(source.key))

    const renderFilters = () => {
        return (
            <>
            <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                <TextField
                    select
                    size="small"
                    label="Tipo de pedido"
                    value={filterSource}
                    onChange={(e) => setFilterSource(e.target.value)}
                    sx={{ width: 190, background: 'white' }}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {visibleSources.map((source) => (
                        <MenuItem key={source.key} value={source.key}>{source.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Dropoff"
                    value={filterDropoff}
                    onChange={(e) => setFilterDropoff(e.target.value)}
                    sx={{ width: 190, background: 'white' }}
                >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="con">Con dropoff</MenuItem>
                    <MenuItem value="sin">Sin dropoff</MenuItem>
                    {dropoffCarrierOptions.length > 1 && <Divider />}
                    {dropoffCarrierOptions.length > 1 && dropoffCarrierOptions.map((carrier) => (
                        <MenuItem key={carrier.value} value={`carrier:${carrier.value}`}>Dropoff {carrier.label}</MenuItem>
                    ))}
                </TextField>
            </Stack>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{mb: 2}}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={3}>
                        <LocalizationProvider size="small" dateAdapter={AdapterDateFns}>
                            <StyledDatePicker
                                label="Desde"
                                format="dd/MM/yyyy"
                                sx={{ background: 'white' }}
                                value={startDate}
                                maxDate={endDate}
                                closeOnSelect={true}
                                slotProps={{
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },
                                }}
                                onChange={(newValue) => {
                                    if(!isCompleteDate(newValue)) { return }
                                    setStartDate(newValue)
                                    // Si la digitada queda después del "Hasta", corremos el otro extremo
                                    // en vez de descartar el cambio.
                                    if(endDate && newValue > endDate) { setEndDate(newValue) }
                                }}
                                onAccept={(newValue) => setStartDate(newValue)}
                                renderInput={(params) => <TextField {...params} />}
                            />
                            <StyledDatePicker
                                label="Hasta"
                                format="dd/MM/yyyy"
                                sx={{ background: 'white' }}
                                value={endDate}
                                minDate={startDate}
                                closeOnSelect={true}
                                slotProps={{
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },
                                }}
                                onChange={(newValue) => {
                                    if(!isCompleteDate(newValue)) { return }
                                    setEndDate(newValue)
                                    if(startDate && newValue < startDate) { setStartDate(newValue) }
                                }}
                                onAccept={(newValue) => setEndDate(newValue)}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </LocalizationProvider>
                        <Autocomplete
                            id="centre-select"
                            size="small"
                            freeSolo
                            sx={{
                                width: 200,
                                boxShadow: (theme) => theme.shadows[25],
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: 0,
                                }
                            }}
                            options={centres}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            value={currCentre}
                            onChange={(e, v) => { if(v) { setCurrCentre(v) } }}
                            renderInput={(params) => <TextField {...params} label="Centro" sx={{background: 'white'}} />}
                        />
                        { renderOptimizingOptions() }
                        <Paper
                            sx={{
                                px: 1,
                                boxShadow: (theme) => theme.shadows[25],
                            }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}>
                                <Typography variant="body2">Opciones</Typography>
                                <Tooltip title={ `${showMap ? 'Ocultar' : 'Mostrar'} mapa` }>
                                    <IconButton
                                        value="check"
                                        variant="contained"
                                        color={ showMap ? 'primary' : '#888' }
                                        size="small"
                                        selected={showMap}
                                        onClick={toggleShowMap}>
                                        <MapOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={ `${showZones ? 'Ocultar' : 'Mostrar'} zonas de despacho en mapa` }>
                                    <IconButton
                                        value="check"
                                        variant="contained"
                                        color={ showZones ? 'primary' : '#888' }
                                        size="small"
                                        selected={showZones}
                                        onClick={() => { setShowZones(!showZones); }}>
                                        <HexagonOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={ `${showProductDetails ? 'Ocultar' : 'Mostrar'} detalle de productos cuando se posiciona sobre una caja` }>
                                    <IconButton
                                        value="check"
                                        variant="contained"
                                        color={ showProductDetails ? 'primary' : '#888' }
                                        size="small"
                                        selected={showProductDetails}
                                        onClick={() => { setShowProductDetails(!showProductDetails); }}>
                                        <InsertCommentOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={ `${showCustomerLocationOnHover ? 'Ocultar' : 'Mostrar'} ubicación de clientes en mapa cuando se posiciona sobre una caja` }>
                                    <IconButton
                                        value="check"
                                        variant="contained"
                                        color={ showCustomerLocationOnHover ? 'primary' : '#888' }
                                        size="small"
                                        selected={showCustomerLocationOnHover}
                                        onClick={() => { setShowCustomerLocationOnHover(!showCustomerLocationOnHover); }}>
                                        <LocationSearchingOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Paper>
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}>
                        <LoadingButton
                            loading={saving}
                            variant="contained"
                            color="success"
                            size='small'
                            endIcon={<SaveOutlinedIcon />}
                            loadingPosition="end"
                            onClick={() => { savePlanification() }}>
                                { saving ? 'Guardando' : 'Guardar' }
                        </LoadingButton>
                    </Stack>
            </Stack>
            </>
        )
    }

    const renderTripsDND = () => {
        return (
            <React.Fragment>
                {_.map(trips, (trip, i) => {
                    const showRoute = !_.includes(hiddenTripsRoutes, i);
                    const route = routes[i];
                    const hasOrderErrors = route?.order_errors?.length > 0;

                    return (
                        <OrdersDND
                            key={trip.uid}
                            droppableId={`plannedOrders_${i}`}
                            title="Ruta"
                            items={ordersWithRouteErrors(trip.orders, route)}
                            mapPickupMode={mapPickupMode}
                            leftActions={
                                <React.Fragment>
                                    {pinsColors[i] && (
                                        <Tooltip title={`${showRoute ? 'Ocultar' : 'Mostrar'} zonas de despacho`}>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    showRoute
                                                        ? setHiddenTripsRoutes([...hiddenTripsRoutes, i])
                                                        : setHiddenTripsRoutes(_.without(hiddenTripsRoutes, i));
                                                }}
                                            >
                                                <RouteOutlinedIcon
                                                    sx={{
                                                        fontSize: 16,
                                                        color: showRoute ? pinsColors[i] : '#DDD',
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {(route?.error && !hasOrderErrors) ? (
                                        <Typography variant="body2" color="error">
                                            {route.error}
                                        </Typography>
                                    ) : (
                                        (route?.duration) && (
                                            <Typography variant="body2">
                                                {printDuration(route.duration)}
                                            </Typography>
                                        )
                                    )}
                                </React.Fragment>
                            }
                            rightActions={
                                <Tooltip arrow title="Asigna dropoff a los pedidos de la ruta">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => openCarrierMenu(e, i)}
                                    >
                                        <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            }
                            groupColor={pinsColors[i]}
                            listLoading={listLoading}
                            isPlanification={true}
                            groupNumber={i + 1}
                            drivers={drivers}
                            transporters={transporters}
                            vehicles={vehicles}
                            selShipDate={trip.ship_date ? new Date(trip.ship_date) : new Date()}
                            selDriver={findTripAssigneeById(trip.driver_id)}
                            selVehicle={_.find(vehicles, { id: trip.vehicle_id })}
                            selectedBoxes={selectionsInGroup(`plannedOrders_${i}`)}
                            onShipDateChange={(v) => {
                                const tempTrips = [...trips];
                                tempTrips[i].ship_date = v;
                                setTrips(tempTrips);
                            }}
                            onSelDriverChange={(e, v) => {
                                const tempTrips = [...trips];
                                tempTrips[i].driver_id = v?.id || null;
                                setTrips(tempTrips);
                            }}
                            onSelVehicleChange={(e, v) => {
                                const tempTrips = [...trips];
                                tempTrips[i].vehicle_id = v?.id || null;
                                setTrips(tempTrips);
                            }}
                            onItemMouseOver={(e, order) =>
                                setItemOnHover({ item: order, kind: 'order', anchorElement: e.currentTarget })
                            }
                            onItemMouseLeave={() => setItemOnHover(null)}
                            onEndPlanification={() => savePlanification([i], true)}
                            onToggleSelection={(droppableId, idx) => toggleSelection(droppableId, idx)}
                            onToggleSelectionInGroup={(droppableId, idx) =>
                                toggleSelectionInGroup(droppableId, idx)
                            }
                        />
                    );
                })}
            </React.Fragment>
        );
    };


    // Map pins color palette
    const pinsColors = [
        '#ab47bc', // Púrpura Orquídea
        '#2196f3', // Azul Cobalto
        '#f44336', // Rojo Carmesí
        '#ff9800', // Naranja Mandarina
        '#ec407a', // Rosa Coral
        '#cddc39', // Verde Lima
        '#4caf50', // Verde Esmeralda
        '#ffc107', // Amarillo Ámbar
        '#00bcd4', // Turquesa
        '#4fc3f7', // Azul Cielo
    ];

    const pinsData = _.flatten(_.map(trips, (t, i) => {
        return (
            {
                position: i+1,
                color: pinsColors[i],
                pins: _.map(t.orders, (o, j) => {
                    return (
                        {
                            ...orderCoords(o),
                            position: j+1,
                            info: `${o.code} - ${o.locatable?.name}`
                        }
                    )
                })
            }
        )
    }))

    const renderOptimizingOptions = () => {
        const routingPlgs = _.filter(authUser?.current_company?.enabled_plugins, (p) => p.category === 'Routing')
        if(routingPlgs.length === 0) return null
        if(!currOptimizer) setCurrOptimizer(routingPlgs[0])

        return(
            <Paper sx={{
                    px: 1,
                    boxShadow: (theme) => theme.shadows[25],
                }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}>
                    <Typography variant="body2">Planificar por</Typography>
                    <FormControl
                        // variant="filled"
                        size="small"
                        sx={{
                            minWidth: 120,
                            '& .MuiInputBase-input': {
                                paddingTop: 0.5,
                                paddingBottom: 0.5,
                                color: (theme) => theme.palette.primary.main,
                                borderWidth: 0,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 0,
                            }
                        }}>
                        <Select
                            labelId="optimize-by"
                            id="optimize-by"
                            value={optimizeByOrders ? OPTIMIZE_BY_ORDERS : currOptimizer?.id}
                            size="small"
                            autoWidth
                            onChange={(e) => {
                                const byOrders = e.target.value === OPTIMIZE_BY_ORDERS
                                setOptimizeByOrders(byOrders)
                                if(!byOrders) {
                                    setCurrOptimizer(_.find(routingPlgs, { id: e.target.value }))
                                    setSelectedOrderIdsToOptimize([])
                                }
                            }}
                        >
                            {_.map(routingPlgs, (p) => {
                                return (
                                    <MenuItem key={`optimizer-${p.id}`} value={p.id}>{optimizerNames(p.slug)}</MenuItem>
                                )
                            })}
                            {/* No es un plugin: usa el mismo optimizador seleccionado,
                                pero acotado a los pedidos marcados en la lista. */}
                            <MenuItem value={OPTIMIZE_BY_ORDERS}>Pedidos</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title={
                        optimizeByOrders
                            ? (selectedOrderIdsToOptimize.length === 0
                                ? 'Marca los pedidos a optimizar'
                                : `Recalcular con ${selectedOrderIdsToOptimize.length} pedido${selectedOrderIdsToOptimize.length === 1 ? '' : 's'}`)
                            : 'Recalcular'
                    }>
                        <span>
                            <IconButton
                                value="check"
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={optimizing || (optimizeByOrders && selectedOrderIdsToOptimize.length === 0)}
                                onClick={optimizeTrips}>
                                <CalculateOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Paper>
        )
    }

    return (
        <div>
            <Typography variant="h2">Planificación</Typography>
            { renderFilters() }
            {/* Sin rutas todavía el espacio de la derecha está vacío, así que el detalle
                se muestra ahí; apenas hay una ruta pasa a mostrarse abajo (o arriba, si
                no cabe) para no taparla. */}
            { showProductDetails && (
                <OrderDeliveryDetailsPopover
                    item={itemOnHover?.item}
                    anchorElement={itemOnHover?.anchorElement}
                    placement={trips.length === 0 ? 'right-start' : 'bottom-start'}
                />
            ) }
            <DragDropContext
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} md={showMap ? 4 : 6}>
                        <OrdersDND
                            droppableId="pendingOrders"
                            title={`Por planificar (${pendingOrders.length}${pendingOrdersMeta?.total_count > pendingOrders.length ? `/${pendingOrdersMeta.total_count}` : ''})${optimizeByOrders && selectedOrderIdsToOptimize.length > 0 ? ` · ${selectedOrderIdsToOptimize.length} marcados` : ''}`}
                            items={visiblePendingOrders}
                            listLoading={listLoading}
                            isPlanification={false}
                            selectedBoxes={pendingOrdersSelectionsForCurrentPage()}
                            showItemCheckbox={optimizeByOrders}
                            checkedItemIds={selectedOrderIdsToOptimize}
                            checkableItemIds={optimizableOrderIds}
                            uncheckableReason="Este pedido no tiene zona de despacho, por lo tanto no se puede optimizar"
                            onToggleItemChecked={toggleOrderToOptimize}
                            rightActions={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        {Math.min(pendingOrdersViewPage, pendingOrdersTotalPages)}/{pendingOrdersTotalPages}
                                    </Typography>
                                    <Tooltip title="Página anterior">
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={pendingOrdersViewPage <= 1 || listLoading || pendingOrdersLoadingMore}
                                                onClick={() => goToPendingOrdersPage(pendingOrdersViewPage - 1)}
                                            >
                                                <NavigateBeforeRoundedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={pendingOrdersLoadingMore ? 'Cargando...' : 'Página siguiente'}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    listLoading ||
                                                    pendingOrdersLoadingMore ||
                                                    !canGoToNextPendingOrdersPage
                                                }
                                                onClick={() => goToPendingOrdersPage(pendingOrdersViewPage + 1)}
                                            >
                                                <NavigateNextRoundedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            }
                            showSearch={true}
                            searchValue={searchQuery}
                            onSearchQueryChange={handlePendingOrdersCardSearchQueryChange}
                            onItemMouseOver={(e, order) => setItemOnHover({ item: order, kind: 'order', anchorElement: e.currentTarget })}
                            onItemMouseLeave={(e, order) => setItemOnHover(null)}
                            onToggleSelection={(droppableId, idx) => {
                                toggleSelection(droppableId, droppableId === 'pendingOrders' ? toPendingOrdersGlobalIndex(idx) : idx)
                            }}
                            onToggleSelectionInGroup={(droppableId, idx) => {
                                toggleSelectionInGroup(droppableId, droppableId === 'pendingOrders' ? toPendingOrdersGlobalIndex(idx) : idx)
                            }} />
                    </Grid>

                    <Grid item xs={12} md={showMap ? 4 : 6}>
                        <OrdersDND
                            droppableId="newTrip"
                            title="Nueva ruta"
                            isPlanification={false}
                            listLoading={listLoading}
                            newGroupContainer={true} />
                        {renderTripsDND()}
                    </Grid>

                    {showMap && (
                    <Grid item xs={12} md={4} sx={{ height: "80vh", display: 'flex', flexDirection: 'column',}}>
                        {mapPickupMode && (
                            <CarrierPickupSearch
                                carrier={mapPickupMode.carrier}
                                onSearch={async (address, options = {}) => {
                                    const { notify = false } = options;
                                    const selectedCarrier = mapPickupMode?.carrier;
                                    const requestId = pickupSearchRequestRef.current + 1;
                                    pickupSearchRequestRef.current = requestId;
                                    setLoadingPickupPoints(true);
                                    try {
                                        const data = await shippingServices.getShippingPoints({
                                            carrier: selectedCarrier,
                                            address,
                                        });

                                        if (requestId !== pickupSearchRequestRef.current) return;

                                        const points = data?.points || [];
                                        const source = data?.source;
                                        const isFakeSource = source === 'fallback_fake' || source === 'fake';
                                        let pointsToDisplay = points;

                                        if (isFakeSource) {
                                            const searchedAddressPoint = await geocodePickupSearchAddress({
                                                address,
                                                carrier: selectedCarrier,
                                            });

                                            if (requestId !== pickupSearchRequestRef.current) return;

                                            if (searchedAddressPoint) {
                                                pointsToDisplay = [searchedAddressPoint];
                                                setCurrMapCenter({
                                                    lat: _.toNumber(searchedAddressPoint.lat),
                                                    lng: _.toNumber(searchedAddressPoint.lng),
                                                });
                                            } else {
                                                pointsToDisplay = points.slice(0, 1).map((point) => ({
                                                    ...point,
                                                    address_hint: address,
                                                }));

                                                if (pointsToDisplay[0]) {
                                                    setCurrMapCenter({
                                                        lat: _.toNumber(pointsToDisplay[0].lat),
                                                        lng: _.toNumber(pointsToDisplay[0].lng),
                                                    });
                                                }
                                            }
                                        }

                                        setPickupPoints(pointsToDisplay);

                                        if (notify && isFakeSource) {
                                            enqueueSnackbar(
                                                `Se están mostrando puntos estimados de ${selectedCarrier} (API no disponible).`,
                                                { variant: 'warning' }
                                            );
                                        } else if (notify && pointsToDisplay.length > 0) {
                                            enqueueSnackbar(
                                                `Se cargaron ${pointsToDisplay.length} puntos reales de ${selectedCarrier}.`,
                                                { variant: 'success' }
                                            );
                                        }

                                        if (notify && pointsToDisplay.length === 0) {
                                            enqueueSnackbar(
                                                `No se encontraron puntos para "${address}".`,
                                                { variant: 'info' }
                                            );
                                        }
                                    } catch (error) {
                                        console.error('Error buscando puntos de carrier:', error);
                                        if (requestId !== pickupSearchRequestRef.current) return;
                                        setPickupPoints([]);
                                        if (notify) enqueueSnackbar(
                                            'No se pudo consultar puntos de retiro. Intenta nuevamente.',
                                            { variant: 'error' }
                                        );
                                    } finally {
                                        if (requestId === pickupSearchRequestRef.current) {
                                            setLoadingPickupPoints(false);
                                        }
                                    }
                                }}
                                onClear={() => {
                                    pickupSearchRequestRef.current += 1;
                                    setPickupPoints([]);
                                    setLoadingPickupPoints(false);
                                }}
                                loading={loadingPickupPoints}
                            />
                        )}

                        <div style={{ flex: 1 }}>
                            <ZonesMapEdit
                                zones={deliveryZones}
                                loading={loadingZones}
                                currentZone={currDeliveryZone}
                                mapCenter={currMapCenter}
                                onCenterChanged={(c) => setCurrMapCenter(c)}
                                onPolygonClick={(dz) => { 
                                    setCurrDeliveryZone(dz); 
                                    setDrawingMode({ mode: null, cancel: false })  
                                }}
                                onCloseInfoWindow={handleOnCloseInfoWindow}
                                onSaveZoneClick={saveDeliveryZone}
                                onDeleteZoneClick={requestDeleteDeliveryZone}
                                onZoneNameChange={handleDeliveryZoneNameChange}
                                onOverlayComplete={onOverlayComplete}
                                onDrawCancel={() => { setDrawingMode({ mode: null, cancel: true }) }}
                                routesPins={pinsData}
                                routes={routes}
                                hiddenRoutes={hiddenTripsRoutes}
                                centres={[currDisplayCentre]}
                                showZones={showZones}
                                pickupPoints={mapPickupMode ? pickupPoints : []}
                                onPickupPointClick={onPickupPointClick}
                                dropoffTrips={trips}
                                preview={
                                    (itemOnHover && showCustomerLocationOnHover) && {
                                        title: itemOnHover.item?.locatable?.name,
                                        ...orderCoords(itemOnHover.item)
                                    }
                                }
                            />
                        </div>
                    </Grid>
                    )}
                </Grid>
            </DragDropContext>
            <Dialog
                open={openAddressDialog}
                // Persistente a propósito: sólo se cierra eligiendo una dirección o
                // con los botones, para no dejar un movimiento a medio camino.
                onClose={(e, reason) => {
                    if(reason === 'backdropClick' || reason === 'escapeKeyDown') return
                    cancelAddressResolution()
                }}
                disableEscapeKeyDown
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    <Typography variant="h3" color="primary">Elegir dirección de entrega</Typography>
                </DialogTitle>
                <DialogContent>
                    {addressResolveQueue.length > 0 && (() => {
                        const order = addressResolveQueue[0]
                        const addresses = order?.locatable?.addresses || []
                        return (
                            <>
                                <Typography sx={{ mb: 1 }}>
                                    El pedido <b>{order?.code}</b>{order?.locatable?.name ? ` (${order.locatable.name})` : ''} tiene más de una dirección. ¿Cuál quieres usar en esta ruta?
                                </Typography>
                                {addressResolveQueue.length > 1 &&
                                    <Typography variant="caption" color="text.secondary">
                                        Quedan {addressResolveQueue.length - 1} pedidos por resolver después de este.
                                    </Typography>
                                }
                                <Stack spacing={1} sx={{ mt: 2 }}>
                                    {addresses.map((addr, idx) => (
                                        <Button
                                            key={`addr-opt-${addr.id}`}
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => chooseAddressForCurrentOrder(addr)}
                                            endIcon={addr.primary ? <PrimaryAddressChip /> : null}
                                            // textAlign porque un <button> centra su texto: la dirección
                                            // que no cabía en una línea quedaba con la segunda centrada.
                                            sx={{ justifyContent: 'space-between', textTransform: 'none', textAlign: 'left' }}
                                        >
                                            {/* Solo la dirección: el "Dirección N" le robaba el ancho y la
                                                partía en dos líneas. El número queda de respaldo para las
                                                que no tienen calle. */}
                                            <span>{[addr.short_address?.trim(), addr.short_zone].filter(Boolean).join(' · ') || `Dirección ${idx + 1}`}</span>
                                        </Button>
                                    ))}
                                </Stack>
                            </>
                        )
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelAddressResolution}>
                        { addressDialogMode === 'optimize' ? 'Omitir este pedido' : 'Cancelar' }
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={dropoffDefaultPrompt.open}
                onClose={closeDropoffDefaultPrompt}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Dirección de dropoff de {dropoffDefaultPrompt.carrierLabel}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <Typography variant="subtitle2">
                            {dropoffDefaultPrompt.defaultAddress?.text}
                        </Typography>

                        {dropoffPromptOrders.length > 1 ? (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Elige a qué pedidos de la ruta asignarle la dirección. A los
                                    marcados que ya tengan otro dropoff se les reemplaza; los
                                    desmarcados quedan como están.
                                </Typography>

                                <Divider />

                                <ListItemButton
                                    dense
                                    onClick={toggleAllDropoffPromptOrders}
                                    sx={{ px: 1 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Checkbox
                                            edge="start"
                                            disableRipple
                                            size="small"
                                            checked={dropoffDefaultPrompt.selectedOrderIds?.length === dropoffPromptOrders.length}
                                            indeterminate={
                                                (dropoffDefaultPrompt.selectedOrderIds?.length || 0) > 0 &&
                                                dropoffDefaultPrompt.selectedOrderIds?.length !== dropoffPromptOrders.length
                                            }
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" fontWeight={500}>
                                                Todos los pedidos ({dropoffDefaultPrompt.selectedOrderIds?.length || 0} de {dropoffPromptOrders.length} seleccionados)
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>

                                <List dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
                                    {dropoffPromptOrders.map((order) => {
                                        const currentDropoff = order.dropoff
                                            ? getDropoffCarrierByKey(order.dropoff_carrier, companySettings?.available_dropoff_carriers)
                                            : null;
                                        // La dirección elegida al planificar manda sobre la principal del cliente.
                                        const location = order.effective_location || order.locatable?.location;
                                        const customerAddress = [
                                            (order.planned_address?.short_address || location?.short_address)?.trim(),
                                            location?.short_zone,
                                        ].filter(Boolean).join(', ');

                                        return (
                                            <ListItemButton
                                                key={`dropoffPromptOrder_${order.id}`}
                                                dense
                                                onClick={() => toggleDropoffPromptOrder(order.id)}
                                                sx={{ px: 1 }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <Checkbox
                                                        edge="start"
                                                        disableRipple
                                                        size="small"
                                                        checked={_.includes(dropoffDefaultPrompt.selectedOrderIds || [], order.id)}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {order.code}
                                                            </Typography>
                                                            {order.dropoff && (
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label={`Ya tiene dropoff${currentDropoff?.label ? ` - ${currentDropoff.label}` : ''}`}
                                                                    sx={{ height: 18, fontSize: 10 }}
                                                                />
                                                            )}
                                                        </Stack>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography variant="body2" color="text.secondary" noWrap component="span" display="block">
                                                                {order.locatable?.name}
                                                            </Typography>
                                                            {customerAddress && (
                                                                <Typography variant="caption" color="text.secondary" component="span" display="block">
                                                                    {customerAddress}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {dropoffPromptOrders.length === 1
                                    ? 'Se asignará al pedido de la ruta, incluso si ya tiene otro dropoff.'
                                    : 'La ruta no tiene pedidos a los que asignar el dropoff.'}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDropoffDefaultPrompt}>Cancelar</Button>
                    <Button onClick={startManualDropoffSelection}>Ingresar una manual</Button>
                    <Button
                        variant="contained"
                        onClick={applyDefaultDropoffToTrip}
                        disabled={(dropoffDefaultPrompt.selectedOrderIds?.length || 0) === 0}
                    >
                        {dropoffPromptOrders.length > 1
                            ? `Asignar a ${dropoffDefaultPrompt.selectedOrderIds?.length || 0} pedido(s)`
                            : 'Dirección por defecto'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Menu
                anchorEl={carrierMenu.anchorEl}
                open={Boolean(carrierMenu.anchorEl)}
                onClose={closeCarrierMenu}
            >
                {dropoffCarrierOptions.map((carrier) => (
                    <MenuItem key={carrier.value} onClick={() => onSelectCarrier(carrier.value, carrierMenu.tripIndex)}>
                        {carrier.label}
                    </MenuItem>
                ))}
            </Menu>
            <Dialog
                open={subscriptionLimitModal.open}
                onClose={() => setSubscriptionLimitModal({ open: false, message: '', used: 0, limit: 0, overBy: 0 })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Limite del plan alcanzado</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            {subscriptionLimitModal.message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Uso actual: {formatQty(subscriptionLimitModal.used)} / {formatQty(subscriptionLimitModal.limit)} rutas.
                        </Typography>
                        {Number(subscriptionLimitModal.overBy || 0) > 0 && (
                            <Typography variant="body2" sx={{ color: ROUTES_LIMIT_COLORS.overBy, fontWeight: 600 }}>
                                Sobreconsumo: {formatQty(subscriptionLimitModal.overBy)} rutas.
                            </Typography>
                        )}
                        <Box sx={{ height: 260, position: 'relative' }}>
                            {(() => {
                                const chartData = buildRoutesLimitChartData(subscriptionLimitModal.used, subscriptionLimitModal.limit);
                                const chartTotal = chartData.reduce((acc, entry) => acc + Number(entry.value || 0), 0);

                                if (chartTotal <= 0) {
                                    return (
                                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Sin datos de consumo.
                                            </Typography>
                                        </Stack>
                                    );
                                }

                                return (
                                    <>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={chartData}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    innerRadius={54}
                                                    outerRadius={84}
                                                    paddingAngle={2}
                                                >
                                                    {chartData.map((entry) => (
                                                        <Cell key={entry.name} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip content={<RoutesLimitTooltip />} />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                {formatQty(subscriptionLimitModal.used)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                rutas usadas
                                            </Typography>
                                        </Box>
                                    </>
                                );
                            })()}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubscriptionLimitModal({ open: false, message: '', used: 0, limit: 0, overBy: 0 })}>
                        Aceptar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setSubscriptionLimitModal({ open: false, message: '', used: 0, limit: 0, overBy: 0 })
                            navigate('/subscriptions')
                        }}
                    >
                        Ver suscripciones
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirmDeleteZone)}
                title="Eliminar zona de reparto"
                message={
                    <>
                        ¿Estás seguro que deseas eliminar la zona <strong>{confirmDeleteZone?.name}</strong>?
                        <br />
                        Se borra la geocerca y los pedidos dejarán de agruparse por ella.
                    </>
                }
                onClose={() => setConfirmDeleteZone(null)}
                onConfirm={() => deleteDeliveryZone()}
            />

        </div>
    );
};

export default PlanificationIndex;
